import {MouseEvent, ReactNode} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {p} from "@pg-design/helpers-css";
import {ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, numberFormat} from "@pg-mono/string-utils";

import {ApplicationSourceSection} from "../application/utils/ApplicationSource";
import {PropertyShowButtonsTestContainer} from "../atoms/PropertyBox/PropertyShowButtonTestContainer";
import {PropertyViewRedirectButton} from "../atoms/PropertyBox/PropertyViewRedirectButton";
import {useResponsiveLinkTarget} from "../hooks/use_responsive_link_target";
import {IMapOfferSidebar} from "../offer/map/types/MapOfferSidebar";
import {IOfferDetail} from "../offer/types/IOfferDetail";
import {OfferType} from "../offer/types/OfferType";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {gtmEventPropertyListClick} from "../tracking/google_tag_manager/gtm_event_property_list";
import {gtmEventPropertyListPrice} from "../tracking/google_tag_manager/gtm_event_property_list_price";
import {PropertyListApplicationButtonVariantBCD} from "./components/property_list_application_button/PropertyListApplicationButtonVariantBCD";
import {PropertyListPlanButton} from "./PropertyListPlanButton";
import {propertiesSortOptions} from "./PropertyListTable";

interface IProps {
    property: IPropertyListProperty;
    offer: IOfferDetail | IMapOfferSidebar;
    sort: string | null;
    onPlanClick: (e: MouseEvent<HTMLElement>, index: number) => void;
    index: number;
    sourceSection?: ApplicationSourceSection;
    showPriceColumn: boolean;
    withoutLink?: boolean;
    hideCell?: {
        propertyPlan?: boolean;
        propertyNumber?: boolean;
        propertyRooms?: boolean;
        propertyArea?: boolean;
        plotArea?: boolean;
        propertyFloor?: boolean;
        propertyPrice?: boolean;
        propertyGotoLink?: boolean;
    };
    handleCellClick?: (value: number) => void;
    textLinkButton?: boolean;
    icon?: ReactNode;
}

export const floorOrFloors = (offerType: OfferType, propertyFloors: number, propertyFloor: number) => {
    if (offerType === OfferType.HOUSE && propertyFloors >= 1) {
        return numberFormat(propertyFloors);
    } else if (propertyFloor != null) {
        if (propertyFloor === 0) {
            return "Parter";
        } else {
            return numberFormat(propertyFloor);
        }
    }
    return "-";
};

export const PropertyListTableRow = (props: IProps) => {
    const {property, offer, sort, onPlanClick, index, sourceSection, showPriceColumn, withoutLink, hideCell, handleCellClick, textLinkButton, icon} = props;

    const theme = useTheme();
    const responsiveLinkTarget = useResponsiveLinkTarget();

    const isActive = (columnSort: string) => sort && (columnSort === sort || `-${columnSort}` === sort);

    const activeNumberClass = isActive(propertiesSortOptions.NUMBER.value) ? "activeCell" : "";
    const activeRoomClass = isActive(propertiesSortOptions.ROOMS.value) ? "activeCell" : "";
    const activePlotAreaClass = isActive(propertiesSortOptions.PLOT_AREA.value) ? "activeCell" : "";
    const activeAreaClass = isActive(propertiesSortOptions.AREA.value) ? "activeCell" : "";
    const activeFloorClass = isActive(propertiesSortOptions.FLOOR.value) ? "activeCell" : "";
    const activePriceClass = isActive(propertiesSortOptions.PRICE.value) ? "activeCell" : "";

    const handleCellClickHandler = () => {
        if (handleCellClick) {
            handleCellClick(index);
        }
        gtmEventPropertyListPrice(index);
        gtmEventPropertyListClick("nieruchomosc");
    };

    const handleLinkClick = () => {
        gtmEventPropertyListPrice(index);
        gtmEventPropertyListClick("nieruchomosc");
    };

    const link = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const [propertyPlan] = property.plan_image_pages;

    return (
        <tr css={tableRow}>
            <PropertyListTableCell
                href={link}
                target={responsiveLinkTarget}
                withoutLink={withoutLink}
                onCellClick={handleCellClickHandler}
                onLinkClick={handleLinkClick}
                data-testid="property-plan"
                planWrapper
                showOnMobile
                hideCell={hideCell?.propertyPlan}
            >
                {property.plan && propertyPlan && (
                    <PropertyListPlanButton
                        index={index}
                        onPlanClick={(e, idx) => {
                            e.stopPropagation();
                            gtmEventPropertyListClick("rzut");
                            onPlanClick(e, idx);
                        }}
                    />
                )}
            </PropertyListTableCell>

            <PropertyListTableCell
                href={link}
                target={responsiveLinkTarget}
                withoutLink={withoutLink}
                onCellClick={handleCellClickHandler}
                onLinkClick={handleLinkClick}
                className={activeNumberClass}
                data-testid="property-number"
                hideCell={hideCell?.propertyNumber}
            >
                <Text variant="body_copy_2" align="center">
                    {property.number}
                </Text>
            </PropertyListTableCell>

            {(offer.type === OfferType.HOUSE || offer.type === OfferType.FLAT) && (
                <PropertyListTableCell
                    href={link}
                    target={responsiveLinkTarget}
                    withoutLink={withoutLink}
                    onCellClick={handleCellClickHandler}
                    onLinkClick={handleLinkClick}
                    className={activeRoomClass}
                    data-testid="property-rooms"
                    showOnMobile
                    hideCell={hideCell?.propertyRooms}
                >
                    <Text variant="body_copy_2" align="center">
                        {property.rooms}
                    </Text>
                </PropertyListTableCell>
            )}

            <PropertyListTableCell
                href={link}
                target={responsiveLinkTarget}
                withoutLink={withoutLink}
                onCellClick={handleCellClickHandler}
                onLinkClick={handleLinkClick}
                className={activeAreaClass}
                data-testid="property-area"
                showOnMobile
                hideCell={hideCell?.propertyArea}
            >
                <Text variant="body_copy_2" align="center">
                    {areaFormat(property.area, {precision: 2})}
                </Text>
            </PropertyListTableCell>

            {offer.type === OfferType.HOUSE ? (
                <PropertyListTableCell
                    href={link}
                    target={responsiveLinkTarget}
                    withoutLink={withoutLink}
                    onCellClick={handleCellClickHandler}
                    onLinkClick={handleLinkClick}
                    className={activePlotAreaClass}
                    data-testid="plot-area"
                    hideCell={hideCell?.plotArea}
                >
                    <Text variant="body_copy_2" align="center">
                        {property.plot_area ? (
                            <>
                                {numberFormat(property.plot_area, {precision: 2})} m<sup>2</sup>
                            </>
                        ) : (
                            "-"
                        )}
                    </Text>
                </PropertyListTableCell>
            ) : (
                <PropertyListTableCell
                    href={link}
                    target={responsiveLinkTarget}
                    withoutLink={withoutLink}
                    onCellClick={handleCellClickHandler}
                    onLinkClick={handleLinkClick}
                    className={activeFloorClass}
                    data-testid="property-floor"
                    hideCell={hideCell?.propertyFloor}
                >
                    <Text variant="body_copy_2" align="center">
                        {floorOrFloors(offer.type, property.floors, property.floor)}
                    </Text>
                </PropertyListTableCell>
            )}

            {showPriceColumn && (
                <PropertyListTableCell
                    href={link}
                    target={responsiveLinkTarget}
                    withoutLink={withoutLink}
                    onCellClick={handleCellClickHandler}
                    onLinkClick={handleLinkClick}
                    className={activePriceClass}
                    css={priceCell}
                    textLinkButton={textLinkButton}
                    data-testid="property-price"
                    showOnMobile
                    hideCell={hideCell?.propertyPrice}
                >
                    <div onClick={(e) => e.stopPropagation()}>
                        <PropertyShowButtonsTestContainer>
                            {[
                                () => (
                                    <PropertyListApplicationButtonVariantBCD
                                        property={property}
                                        vendorId={offer.vendor.id}
                                        isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                        applicationSourceSection={sourceSection}
                                        index={index}
                                    />
                                ),
                                () => <PropertyViewRedirectButton buttonVariant="highlight_primary" property={property} link={link} />
                            ]}
                        </PropertyShowButtonsTestContainer>
                    </div>
                </PropertyListTableCell>
            )}

            <PropertyListTableCell
                href={link}
                target={responsiveLinkTarget}
                withoutLink={withoutLink}
                onCellClick={handleCellClickHandler}
                onLinkClick={handleLinkClick}
                data-testid="property-goto-link"
                showOnMobile
                hideCell={hideCell?.propertyGotoLink}
            >
                <div css={arrowWrapper}>{icon ? icon : <ChevronRightIcon size="3" fill={theme.colors.gray[700]} />}</div>
            </PropertyListTableCell>
        </tr>
    );
};

interface PropertyListTableCellProps {
    children: React.ReactNode;

    href?: string;
    target?: string;
    withoutLink?: boolean;

    onCellClick?: () => void;
    onLinkClick?: () => void;

    className?: string;
    showOnMobile?: boolean;
    hideCell?: boolean;
    css?: ReturnType<typeof css>;
    textLinkButton?: boolean;
    planWrapper?: boolean;

    "data-testid"?: string;
}

export const PropertyListTableCell = ({children, href, target, withoutLink, onCellClick, onLinkClick, ...rowCellProps}: PropertyListTableCellProps) => {
    if (withoutLink || !href) {
        return (
            <RowCell {...rowCellProps} onClick={onCellClick}>
                {children}
            </RowCell>
        );
    }

    return (
        <RowCell {...rowCellProps}>
            <a href={href} target={target} onClick={onLinkClick} css={tableRow} style={{display: "contents"}}>
                {children}
            </a>
        </RowCell>
    );
};

const tableRow = (props: Theme) => css`
    transition: background 0.2s ease-in-out;
    cursor: pointer;
    color: ${props.colors.secondary};

    &:nth-of-type(odd) {
        background: ${props.colors.gray[100]};

        @media (min-width: ${props.breakpoints.sm}) {
            background-color: transparent;
        }
    }

    &:nth-of-type(even) {
        background: ${props.colors.gray[200]};
    }

    &:hover {
        background: ${props.colors.gray[400]};
    }

    .activeCell {
        background: ${props.colors.background[200]};
    }
`;

const arrowWrapper = css`
    transform: scale(50%, 50%);
    width: 100%;
    height: 100%;
    display: flex;
    justify-items: center;
    align-items: center;
`;

interface IRowProps {
    showOnMobile?: boolean;
    planWrapper?: boolean;
    hideCell?: boolean;
    textLinkButton?: boolean;
}

const RowCell = styled.td<IRowProps>`
    vertical-align: middle;
    min-height: 54px;
    height: 54px;
    ${p(1)};
    border: 0;

    @media (min-width: ${(props) => props.theme.breakpoints.xs}) {
        min-height: 64px;
    }

    ${(props) => css`
        display: ${props.showOnMobile && !props.hideCell ? "table-cell" : "none"};
        @media (min-width: ${props.theme.breakpoints.sm}) {
            display: ${props.hideCell ? "none" : "table-cell"};
        }
    `}

    ${(props) =>
        props.planWrapper &&
        css`
            padding-left: 2rem;
            @media (min-width: ${props.theme.breakpoints.xs}) {
                padding: 0 0 0 3rem;
            }
        `}
    
    ${(props) =>
        props.textLinkButton &&
        css`
            button {
                font-style: normal;
                font-weight: normal;
                text-transform: lowercase;
                background-color: transparent;
                border: none;
                padding-left: 0;

                &:hover,
                &:active,
                &:focus,
                &:focus:active {
                    background-color: transparent;
                    text-decoration: underline;
                }

                span:first-letter {
                    text-transform: uppercase;
                }
            }
        `}
`;

const priceCell = css`
    text-align: center;
`;
import {MouseEvent, ReactNode} from "react";
import {css, Theme, useTheme} from "@emotion/react";
import styled from "@emotion/styled";

import {p} from "@pg-design/helpers-css";
import {ChevronRightIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, numberFormat} from "@pg-mono/string-utils";

import {ApplicationSourceSection} from "../application/utils/ApplicationSource";
import {PropertyShowButtonsTestContainer} from "../atoms/PropertyBox/PropertyShowButtonTestContainer";
import {PropertyViewRedirectButton} from "../atoms/PropertyBox/PropertyViewRedirectButton";
import {useResponsiveLinkTarget} from "../hooks/use_responsive_link_target";
import {IMapOfferSidebar} from "../offer/map/types/MapOfferSidebar";
import {IOfferDetail} from "../offer/types/IOfferDetail";
import {OfferType} from "../offer/types/OfferType";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {gtmEventPropertyListClick} from "../tracking/google_tag_manager/gtm_event_property_list";
import {gtmEventPropertyListPrice} from "../tracking/google_tag_manager/gtm_event_property_list_price";
import {PropertyListApplicationButtonVariantBCD} from "./components/property_list_application_button/PropertyListApplicationButtonVariantBCD";
import {PropertyListPlanButton} from "./PropertyListPlanButton";
import {propertiesSortOptions} from "./PropertyListTable";

interface IProps {
    property: IPropertyListProperty;
    offer: IOfferDetail | IMapOfferSidebar;
    sort: string | null;
    onPlanClick: (e: MouseEvent<HTMLElement>, index: number) => void;
    index: number;
    sourceSection?: ApplicationSourceSection;
    showPriceColumn: boolean;
    withoutLink?: boolean;
    hideCell?: {
        propertyPlan?: boolean;
        propertyNumber?: boolean;
        propertyRooms?: boolean;
        propertyArea?: boolean;
        plotArea?: boolean;
        propertyFloor?: boolean;
        propertyPrice?: boolean;
        propertyGotoLink?: boolean;
    };
    handleCellClick?: (value: number) => void;
    textLinkButton?: boolean;
    icon?: ReactNode;
}

export const floorOrFloors = (offerType: OfferType, propertyFloors: number, propertyFloor: number) => {
    if (offerType === OfferType.HOUSE && propertyFloors >= 1) {
        return numberFormat(propertyFloors);
    } else if (propertyFloor != null) {
        if (propertyFloor === 0) {
            return "Parter";
        } else {
            return numberFormat(propertyFloor);
        }
    }
    return "-";
};

export const PropertyListTableRow = (props: IProps) => {
    const {property, offer, sort, onPlanClick, index, sourceSection, showPriceColumn, withoutLink, hideCell, handleCellClick, textLinkButton, icon} = props;

    const theme = useTheme();
    const responsiveLinkTarget = useResponsiveLinkTarget();

    const isActive = (columnSort: string) => sort && (columnSort === sort || `-${columnSort}` === sort);

    const activeNumberClass = isActive(propertiesSortOptions.NUMBER.value) ? "activeCell" : "";
    const activeRoomClass = isActive(propertiesSortOptions.ROOMS.value) ? "activeCell" : "";
    const activePlotAreaClass = isActive(propertiesSortOptions.PLOT_AREA.value) ? "activeCell" : "";
    const activeAreaClass = isActive(propertiesSortOptions.AREA.value) ? "activeCell" : "";
    const activeFloorClass = isActive(propertiesSortOptions.FLOOR.value) ? "activeCell" : "";
    const activePriceClass = isActive(propertiesSortOptions.PRICE.value) ? "activeCell" : "";

    const handleCellClickHandler = () => {
        if (handleCellClick) {
            handleCellClick(index);
        }
        gtmEventPropertyListPrice(index);
        gtmEventPropertyListClick("nieruchomosc");
    };

    const handleLinkClick = () => {
        gtmEventPropertyListPrice(index);
        gtmEventPropertyListClick("nieruchomosc");
    };

    const link = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const [propertyPlan] = property.plan_image_pages;

    return (
        <tr css={tableRow}>
            <PropertyListTableCell
                href={link}
                target={responsiveLinkTarget}
                withoutLink={withoutLink}
                onCellClick={handleCellClickHandler}
                onLinkClick={handleLinkClick}
                data-testid="property-plan"
                planWrapper
                showOnMobile
                hideCell={hideCell?.propertyPlan}
            >
                {property.plan && propertyPlan && (
                    <PropertyListPlanButton
                        index={index}
                        onPlanClick={(e, idx) => {
                            e.stopPropagation();
                            gtmEventPropertyListClick("rzut");
                            onPlanClick(e, idx);
                        }}
                    />
                )}
            </PropertyListTableCell>

            <PropertyListTableCell
                href={link}
                target={responsiveLinkTarget}
                withoutLink={withoutLink}
                onCellClick={handleCellClickHandler}
                onLinkClick={handleLinkClick}
                className={activeNumberClass}
                data-testid="property-number"
                hideCell={hideCell?.propertyNumber}
            >
                <Text variant="body_copy_2" align="center">
                    {property.number}
                </Text>
            </PropertyListTableCell>

            {(offer.type === OfferType.HOUSE || offer.type === OfferType.FLAT) && (
                <PropertyListTableCell
                    href={link}
                    target={responsiveLinkTarget}
                    withoutLink={withoutLink}
                    onCellClick={handleCellClickHandler}
                    onLinkClick={handleLinkClick}
                    className={activeRoomClass}
                    data-testid="property-rooms"
                    showOnMobile
                    hideCell={hideCell?.propertyRooms}
                >
                    <Text variant="body_copy_2" align="center">
                        {property.rooms}
                    </Text>
                </PropertyListTableCell>
            )}

            <PropertyListTableCell
                href={link}
                target={responsiveLinkTarget}
                withoutLink={withoutLink}
                onCellClick={handleCellClickHandler}
                onLinkClick={handleLinkClick}
                className={activeAreaClass}
                data-testid="property-area"
                showOnMobile
                hideCell={hideCell?.propertyArea}
            >
                <Text variant="body_copy_2" align="center">
                    {areaFormat(property.area, {precision: 2})}
                </Text>
            </PropertyListTableCell>

            {offer.type === OfferType.HOUSE ? (
                <PropertyListTableCell
                    href={link}
                    target={responsiveLinkTarget}
                    withoutLink={withoutLink}
                    onCellClick={handleCellClickHandler}
                    onLinkClick={handleLinkClick}
                    className={activePlotAreaClass}
                    data-testid="plot-area"
                    hideCell={hideCell?.plotArea}
                >
                    <Text variant="body_copy_2" align="center">
                        {property.plot_area ? (
                            <>
                                {numberFormat(property.plot_area, {precision: 2})} m<sup>2</sup>
                            </>
                        ) : (
                            "-"
                        )}
                    </Text>
                </PropertyListTableCell>
            ) : (
                <PropertyListTableCell
                    href={link}
                    target={responsiveLinkTarget}
                    withoutLink={withoutLink}
                    onCellClick={handleCellClickHandler}
                    onLinkClick={handleLinkClick}
                    className={activeFloorClass}
                    data-testid="property-floor"
                    hideCell={hideCell?.propertyFloor}
                >
                    <Text variant="body_copy_2" align="center">
                        {floorOrFloors(offer.type, property.floors, property.floor)}
                    </Text>
                </PropertyListTableCell>
            )}

            {showPriceColumn && (
                <PropertyListTableCell
                    href={link}
                    target={responsiveLinkTarget}
                    withoutLink={withoutLink}
                    onCellClick={handleCellClickHandler}
                    onLinkClick={handleLinkClick}
                    className={activePriceClass}
                    css={priceCell}
                    textLinkButton={textLinkButton}
                    data-testid="property-price"
                    showOnMobile
                    hideCell={hideCell?.propertyPrice}
                >
                    <div onClick={(e) => e.stopPropagation()}>
                        <PropertyShowButtonsTestContainer>
                            {[
                                () => (
                                    <PropertyListApplicationButtonVariantBCD
                                        property={property}
                                        vendorId={offer.vendor.id}
                                        isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                        applicationSourceSection={sourceSection}
                                        index={index}
                                    />
                                ),
                                () => <PropertyViewRedirectButton buttonVariant="highlight_primary" property={property} link={link} />
                            ]}
                        </PropertyShowButtonsTestContainer>
                    </div>
                </PropertyListTableCell>
            )}

            <PropertyListTableCell
                href={link}
                target={responsiveLinkTarget}
                withoutLink={withoutLink}
                onCellClick={handleCellClickHandler}
                onLinkClick={handleLinkClick}
                data-testid="property-goto-link"
                showOnMobile
                hideCell={hideCell?.propertyGotoLink}
            >
                <div css={arrowWrapper}>{icon ? icon : <ChevronRightIcon size="3" fill={theme.colors.gray[700]} />}</div>
            </PropertyListTableCell>
        </tr>
    );
};

interface PropertyListTableCellProps {
    children: React.ReactNode;

    href?: string;
    target?: string;
    withoutLink?: boolean;

    onCellClick?: () => void;
    onLinkClick?: () => void;

    className?: string;
    showOnMobile?: boolean;
    hideCell?: boolean;
    css?: ReturnType<typeof css>;
    textLinkButton?: boolean;
    planWrapper?: boolean;

    "data-testid"?: string;
}

export const PropertyListTableCell = ({children, href, target, withoutLink, onCellClick, onLinkClick, ...rowCellProps}: PropertyListTableCellProps) => {
    if (withoutLink || !href) {
        return (
            <RowCell {...rowCellProps} onClick={onCellClick}>
                {children}
            </RowCell>
        );
    }

    return (
        <RowCell {...rowCellProps}>
            <a href={href} target={target} onClick={onLinkClick} css={tableRow} style={{display: "contents"}}>
                {children}
            </a>
        </RowCell>
    );
};

const tableRow = (props: Theme) => css`
    transition: background 0.2s ease-in-out;
    cursor: pointer;
    color: ${props.colors.secondary};

    &:nth-of-type(odd) {
        background: ${props.colors.gray[100]};

        @media (min-width: ${props.breakpoints.sm}) {
            background-color: transparent;
        }
    }

    &:nth-of-type(even) {
        background: ${props.colors.gray[200]};
    }

    &:hover {
        background: ${props.colors.gray[400]};
    }

    .activeCell {
        background: ${props.colors.background[200]};
    }
`;

const arrowWrapper = css`
    transform: scale(50%, 50%);
    width: 100%;
    height: 100%;
    display: flex;
    justify-items: center;
    align-items: center;
`;

interface IRowProps {
    showOnMobile?: boolean;
    planWrapper?: boolean;
    hideCell?: boolean;
    textLinkButton?: boolean;
}

const RowCell = styled.td<IRowProps>`
    vertical-align: middle;
    min-height: 54px;
    height: 54px;
    ${p(1)};
    border: 0;

    @media (min-width: ${(props) => props.theme.breakpoints.xs}) {
        min-height: 64px;
    }

    ${(props) => css`
        display: ${props.showOnMobile && !props.hideCell ? "table-cell" : "none"};
        @media (min-width: ${props.theme.breakpoints.sm}) {
            display: ${props.hideCell ? "none" : "table-cell"};
        }
    `}

    ${(props) =>
        props.planWrapper &&
        css`
            padding-left: 2rem;
            @media (min-width: ${props.theme.breakpoints.xs}) {
                padding: 0 0 0 3rem;
            }
        `}
    
    ${(props) =>
        props.textLinkButton &&
        css`
            button {
                font-style: normal;
                font-weight: normal;
                text-transform: lowercase;
                background-color: transparent;
                border: none;
                padding-left: 0;

                &:hover,
                &:active,
                &:focus,
                &:focus:active {
                    background-color: transparent;
                    text-decoration: underline;
                }

                span:first-letter {
                    text-transform: uppercase;
                }
            }
        `}
`;

const priceCell = css`
    text-align: center;
`;
